// Client-side astrology helpers: decans + stelliums, computed from the natal chart.
// Pure/deterministic — no AI or network needed, works for every existing profile.

export const ZODIAC = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];

export const SIGN_GLYPH = {
  Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋", Leo: "♌", Virgo: "♍",
  Libra: "♎", Scorpio: "♏", Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓",
};

export const PLANET_GLYPH = {
  Sun: "☉", Moon: "☽", Mercury: "☿", Venus: "♀", Mars: "♂", Jupiter: "♃",
  Saturn: "♄", Uranus: "♅", Neptune: "♆", Pluto: "♇", Rising: "Asc", Ascendant: "Asc",
};

export const RULER = {
  Aries: "Mars", Taurus: "Venus", Gemini: "Mercury", Cancer: "Moon", Leo: "Sun",
  Virgo: "Mercury", Libra: "Venus", Scorpio: "Pluto", Sagittarius: "Jupiter",
  Capricorn: "Saturn", Aquarius: "Uranus", Pisces: "Neptune",
};

export const ELEMENT = {
  Aries: "Fire", Leo: "Fire", Sagittarius: "Fire",
  Taurus: "Earth", Virgo: "Earth", Capricorn: "Earth",
  Gemini: "Air", Libra: "Air", Aquarius: "Air",
  Cancer: "Water", Scorpio: "Water", Pisces: "Water",
};

const TRIPLICITY = {
  Fire: ["Aries", "Leo", "Sagittarius"],
  Earth: ["Taurus", "Virgo", "Capricorn"],
  Air: ["Gemini", "Libra", "Aquarius"],
  Water: ["Cancer", "Scorpio", "Pisces"],
};

const ORDINAL = { 1: "1st", 2: "2nd", 3: "3rd" };

// The three sub-signs of a sign's decans (triplicity method), in order.
function decanSubSigns(sign) {
  const el = ELEMENT[sign];
  if (!el) return [sign, sign, sign];
  const arr = TRIPLICITY[el];
  const i = arr.indexOf(sign);
  return [arr[i], arr[(i + 1) % 3], arr[(i + 2) % 3]];
}

// { num, ordinal, subSign, subRuler, element, label }
export function decanOf(sign, degree) {
  if (!sign) return null;
  const deg = typeof degree === "string" ? parseFloat(degree) : degree;
  const d = Number.isFinite(deg) ? deg : 0;
  const num = Math.min(3, Math.max(1, Math.floor(d / 10) + 1));
  const subSign = decanSubSigns(sign)[num - 1];
  const subRuler = RULER[subSign];
  return {
    num,
    ordinal: ORDINAL[num],
    subSign,
    subRuler,
    element: ELEMENT[sign],
    label: `${ORDINAL[num]} decan · ${subSign} (${subRuler})`,
  };
}

// Normalize a natal object into a list of placements with degrees.
// Prefers planet_houses (has every planet + degree); falls back to sign fields.
export function placementsFrom(natal) {
  if (!natal) return [];
  if (Array.isArray(natal.planet_houses) && natal.planet_houses.length) {
    return natal.planet_houses.map((p) => ({
      planet: p.planet,
      sign: p.sign,
      degree: p.degree,
      house: p.house,
    }));
  }
  const fields = [
    ["Sun", natal.sun_sign, natal.sun_degree],
    ["Moon", natal.moon_sign, natal.moon_degree],
    ["Mercury", natal.mercury_sign], ["Venus", natal.venus_sign], ["Mars", natal.mars_sign],
    ["Jupiter", natal.jupiter_sign], ["Saturn", natal.saturn_sign],
  ];
  return fields
    .filter(([, s]) => s)
    .map(([planet, sign, degree]) => ({ planet, sign, degree: degree ?? null, house: null }));
}

// Stelliums = 3+ planets in the same sign. Returns [{sign, element, planets:[...]}].
export function stelliumsOf(natal) {
  const bySign = {};
  for (const p of placementsFrom(natal)) {
    if (!p.sign) continue;
    (bySign[p.sign] = bySign[p.sign] || []).push(p.planet);
  }
  return Object.entries(bySign)
    .filter(([, planets]) => planets.length >= 3)
    .map(([sign, planets]) => ({ sign, element: ELEMENT[sign], planets }))
    .sort((a, b) => b.planets.length - a.planets.length);
}
